"use client";

import { cn } from "@/lib";
import AppHeader from "../appHeader";
import AppContainer from "../appContainer";
import { FlexCenterCol } from "../ui/flexBox";
import { BoCheckCircle } from "solar-icon-react/bo";
import HeroTitle from "../heroTitle";
import { ReactNode, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { FEATURES_MAP, landingFeatures } from "@/constants";
import Image from "next/image";
import WavyPattern from "public/wavy-pattern-2.svg";
import useTailwindBreakpoints from "@/hooks/useIsLargeScreen";
import { AnimatePresence, motion } from "framer-motion";

const HERO_PHRASES = [
  "استعلام و پرداخت خلافی خودرو در چند دقیقه",
  "استعلام فوری شماره شبا با شماره کارت یا حساب",
  "بررسی وضعیت چک برگشتی به‌صورت آنلاین",
  "استعلام وضعیت نظام وظیفه در لحظه",
  "استعلام امتیاز منفی گواهینامه رانندگی",
  "استعلام مشخصات خودرو و موتورسیکلت با پلاک",
  "بررسی اعتبار کارت بانکی به‌صورت آنی",
  "استعلام اطلاعات ثبتی شرکت‌ها و اعضای هیئت‌مدیره",
];

const PHRASE_HOLD_MS = 3200;

function HeroCyclingPhrases() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setIndex((i) => (i + 1) % HERO_PHRASES.length),
      PHRASE_HOLD_MS,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex h-8 items-center justify-center overflow-hidden xl:h-10">
      <AnimatePresence mode="wait">
        <motion.p
          key={index}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -18 }}
          transition={{ duration: 0.45, ease: "easeInOut" }}
          className="text-center text-sm font-medium text-white/90 sm:text-base xl:text-lg"
        >
          {HERO_PHRASES[index]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

const getInquiryHistoryPageFeature = (serviceId?: number): string[] => {
  if (!serviceId) return [];
  return FEATURES_MAP[serviceId] ?? [];
};

const HeroSection = ({
  title,
  isH1 = false,
  features = landingFeatures,
  useMap = false,
  inquiryId,
  hideFeatures = false,
  noIntro = false,
  className,
  translateFeatures = true,
}: {
  isH1?: boolean;
  title?: ReactNode;
  features?: string[];
  useMap?: boolean;
  inquiryId?: number;
  hideFeatures?: boolean;
  noIntro?: boolean;
  className?: string;
  translateFeatures?: boolean;
}) => {
  const t = useTranslations();
  const { isMobile } = useTailwindBreakpoints();
  const [featuresArray, setFeaturesArray] = useState<string[]>(features);

  useEffect(() => {
    if (useMap) {
      setFeaturesArray(getInquiryHistoryPageFeature(inquiryId));
    }
  }, [useMap]);
  return (
    <>
      <section className="relative mb-20 w-full max-w-full overflow-x-clip">
        <div
          className={cn(
            "from-primary-main to-hero-section-main relative isolate h-[270] max-w-full overflow-x-clip bg-radial md:h-[500] xl:h-[680]",
            className,
          )}
        >
          {!isMobile && (
            <div
              className={cn(
                "hero-wavy-pattern pointer-events-none absolute inset-x-0 -inset-y-8 z-10 opacity-50",
              )}
            >
              <Image
                loading="eager"
                src={WavyPattern}
                alt="hero section"
                title="hero section"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
          )}
          <AppContainer className={cn("relative z-20 flex h-full flex-col")}>
            <AppHeader />
            <FlexCenterCol
              className={
                "absolute inset-0 -z-20 h-full gap-4 text-white xl:gap-8"
              }
            >
              {isH1 ? (
                <h1 className="text-xl font-bold sm:text-2xl xl:text-4xl">
                  {title}
                </h1>
              ) : (
                <HeroTitle>{title}</HeroTitle>
              )}
              {hideFeatures ? (
                <>{!noIntro && <HeroCyclingPhrases />}</>
              ) : (
                <ul className="flex-wrap items-center justify-center gap-4 px-3 sm:gap-6 md:flex md:flex-nowrap md:justify-between xl:gap-8">
                  {featuresArray.map((feature, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-1 xl:gap-2"
                    >
                      <BoCheckCircle className="text-secondary-main!" />
                      <h2 className="text-xs sm:text-sm xl:text-base">
                        {translateFeatures ? t(feature as any) : feature}
                      </h2>
                    </li>
                  ))}
                </ul>
              )}
            </FlexCenterCol>
          </AppContainer>
        </div>
      </section>
    </>
  );
};

HeroSection.Skeleton = () => {
  return (
    <section className="relative mb-20 w-full max-w-full overflow-hidden">
      <div
        className={cn(
          "from-primary-main to-hero-section-main relative isolate h-[270] max-w-full overflow-hidden bg-radial md:h-[500] xl:h-[680]",
        )}
      >
        <div className="absolute inset-0 w-full">
          <Image
            loading="eager"
            src={WavyPattern}
            alt="hero section"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </div>
        <AppContainer className="relative z-20 flex h-full flex-col">
          {/* Header Skeleton */}
          <header className="hidden items-center justify-between pt-10 md:flex">
            <div className="flex items-center gap-3">
              <div className="h-[70px] w-[70px] animate-pulse rounded-full bg-gray-300/30" />
              <div className="h-6 w-40 animate-pulse rounded bg-gray-300/30" />
            </div>

            <nav className="hidden basis-1/2 md:block">
              <div className="flex justify-between gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-4 w-24 animate-pulse rounded bg-gray-300/30"
                  />
                ))}
              </div>
            </nav>

            <div className="h-10 w-32 animate-pulse rounded-xl bg-gray-300/30" />
          </header>

          {/* Hero Content Skeleton */}
          <div className="absolute inset-0 -z-20 flex h-full flex-col items-center justify-center gap-4 text-white xl:gap-8">
            <div className="flex flex-col items-center gap-5">
              <div className="h-8 w-64 animate-pulse rounded bg-gray-300/30 sm:h-10 sm:w-80 xl:h-12 xl:w-96" />
              <div className="flex gap-2">
                <div className="h-6 w-32 animate-pulse rounded bg-gray-300/30 sm:w-40" />
                <div className="h-6 w-20 animate-pulse rounded bg-gray-300/30" />
                <div className="h-6 w-32 animate-pulse rounded bg-gray-300/30 sm:w-40" />
              </div>
            </div>

            <ul className="flex w-full flex-wrap items-center justify-center gap-6 px-3 md:flex-nowrap md:justify-between xl:gap-8">
              {[1, 2, 3].map((i) => (
                <li key={i} className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-pulse rounded-full bg-gray-300/30" />
                  <div className="h-4 w-32 animate-pulse rounded bg-gray-300/30" />
                </li>
              ))}
            </ul>
          </div>
        </AppContainer>
      </div>
    </section>
  );
};

export default HeroSection;
